/**
 * Reusable progress banner shown above scene grids in
 * Scene-To-Video, Roll-Your-Own, and Auto-Video so users always know:
 *  • what stage they're in (uploading → queued → animating → stitching → done)
 *  • how many scenes are done / running / failed
 *  • overall progress %
 *  • rough ETA (~75 s per remaining Veo 3 scene)
 *
 * Props:
 *  - stage:       "uploading" | "queued" | "animating" | "stitching" | "complete" | "failed"
 *  - uploadPct:   0–100 (only used when stage === "uploading")
 *  - total:       total scene count
 *  - ready:       scenes finished (ready)
 *  - animating:   scenes currently animating
 *  - queued:      scenes queued
 *  - failed:      scenes failed
 *  - jobLabel:    optional sub-label (e.g. job_id · title)
 *  - testIdPrefix: prefix for data-testid attributes
 */
export default function JobProgressBanner({
  stage,
  uploadPct = 0,
  total = 0,
  ready = 0,
  animating = 0,
  queued = 0,
  failed = 0,
  jobLabel = "",
  testIdPrefix = "job-progress",
}) {
  // Determine percentage. Upload stage uses uploadPct; otherwise count
  // ready scenes (a stitching-in-progress job still counts as 100% scenes
  // ready since all Veo 3 clips are complete).
  let pct = 0;
  if (stage === "uploading") {
    pct = Math.max(0, Math.min(100, Math.round(uploadPct)));
  } else if (stage === "complete") {
    pct = 100;
  } else if (total > 0) {
    pct = Math.round((ready / total) * 100);
  }

  // Rough ETA: ~75s per remaining Veo 3 animation. Stitching/upload
  // ETAs are skipped — the upload bar already shows real progress.
  const remaining = Math.max(0, animating + queued);
  const etaSec = remaining * 75;
  const etaLabel =
    stage === "animating" || stage === "queued"
      ? etaSec >= 60
        ? `~${Math.ceil(etaSec / 60)} min remaining`
        : remaining > 0
          ? `~${etaSec}s remaining`
          : ""
      : "";

  const isFailed = stage === "failed";
  const isComplete = stage === "complete";
  const isWorking = !isFailed && !isComplete;

  const barColor = isFailed
    ? "bg-red-500"
    : isComplete
      ? "bg-emerald-400"
      : "bg-yellow-400";

  const stageLabel = {
    uploading: "Uploading stills",
    queued: "Queued",
    animating: "Animating with Veo 3",
    stitching: "Stitching final video",
    complete: "Complete",
    failed: "Failed",
  }[stage] || "Working";

  return (
    <div
      data-testid={`${testIdPrefix}-banner`}
      data-stage={stage}
      className={`border rounded-sm p-4 mb-4 bg-zinc-950/80 backdrop-blur-sm sticky top-0 z-20 ${
        isFailed
          ? "border-red-500/60"
          : isComplete
            ? "border-emerald-400/60"
            : "border-yellow-400/60"
      }`}
    >
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3 min-w-0">
          {isWorking && (
            <span
              className="w-2.5 h-2.5 rounded-full bg-yellow-400 animate-pulse shrink-0"
              aria-hidden
            />
          )}
          <div className="min-w-0">
            <div
              className={`font-mono text-[11px] uppercase tracking-[0.3em] font-bold ${
                isFailed
                  ? "text-red-400"
                  : isComplete
                    ? "text-emerald-300"
                    : "text-yellow-300"
              }`}
              data-testid={`${testIdPrefix}-stage`}
            >
              {stageLabel}
            </div>
            {jobLabel ? (
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 truncate">
                {jobLabel}
              </div>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {total > 0 && (
            <div
              className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400"
              data-testid={`${testIdPrefix}-counts`}
            >
              <span className="text-emerald-300">{ready}</span>
              <span className="text-zinc-600"> / </span>
              <span className="text-zinc-200">{total}</span>
              <span className="text-zinc-500"> ready</span>
              {animating > 0 && (
                <>
                  <span className="text-zinc-700 mx-1">·</span>
                  <span className="text-yellow-300">{animating}</span>
                  <span className="text-zinc-500"> animating</span>
                </>
              )}
              {queued > 0 && (
                <>
                  <span className="text-zinc-700 mx-1">·</span>
                  <span className="text-zinc-300">{queued}</span>
                  <span className="text-zinc-500"> queued</span>
                </>
              )}
              {failed > 0 && (
                <>
                  <span className="text-zinc-700 mx-1">·</span>
                  <span className="text-red-400">{failed}</span>
                  <span className="text-zinc-500"> failed</span>
                </>
              )}
            </div>
          )}
          <div
            className="font-mono text-sm font-bold text-zinc-100 tabular-nums"
            data-testid={`${testIdPrefix}-pct`}
          >
            {pct}%
          </div>
        </div>
      </div>
      <div className="mt-3 h-2 bg-zinc-900 rounded-sm overflow-hidden border border-zinc-800">
        <div
          data-testid={`${testIdPrefix}-bar`}
          className={`h-full ${barColor} transition-[width] duration-500 ease-out ${
            isWorking ? "shadow-[0_0_12px_rgba(250,204,21,0.4)]" : ""
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {etaLabel ? (
        <div
          data-testid={`${testIdPrefix}-eta`}
          className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500"
        >
          {etaLabel}
        </div>
      ) : null}
    </div>
  );
}
